import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { AlertCircle, Plus, Trash2, Bell } from 'lucide-react';

export default function AlertsManager() {
  const [alerts, setAlerts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    keywords: '',
    regions: '',
    threat_level: 'Medium',
    priority: 'Normal',
  });

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const createAlert = async () => {
    try {
      await axios.post(`${API}/alerts`, {
        keywords: formData.keywords.split(',').map(k => k.trim()),
        regions: formData.regions.split(',').map(r => r.trim()),
        threat_level: formData.threat_level,
        priority: formData.priority,
      });
      setFormData({ keywords: '', regions: '', threat_level: 'Medium', priority: 'Normal' });
      setShowForm(false);
      loadAlerts();
    } catch (error) {
      console.error('Error creating alert:', error);
    }
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/alerts/${id}`);
      loadAlerts();
    } catch (error) {
      console.error('Error deleting alert:', error);
    }
  };

  return (
    <div className="h-full overflow-hidden flex flex-col" data-testid="alerts-manager">
      <div className="glass-panel border-b border-white/10 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>CUSTOM ALERTS SYSTEM</h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">Automated monitoring with push notifications</p>
          </div>
          <button
            data-testid="create-alert-button"
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 btn-primary"
          >
            <Plus className="w-4 h-4" />
            <span>CREATE ALERT</span>
          </button>
        </div>
      </div>

      {showForm && (
        <div className="glass-panel border-b border-white/10 p-6">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>NEW ALERT</h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Keywords (comma-separated)</label>
              <input
                data-testid="alert-keywords-input"
                type="text"
                value={formData.keywords}
                onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono outline-none"
                placeholder="missile test, naval exercise"
              />
            </div>
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Regions (comma-separated)</label>
              <input
                type="text"
                value={formData.regions}
                onChange={(e) => setFormData({...formData, regions: e.target.value})}
                className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono outline-none"
                placeholder="Middle East, Asia Pacific"
              />
            </div>
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Threat Level</label>
              <select
                value={formData.threat_level}
                onChange={(e) => setFormData({...formData, threat_level: e.target.value})}
                className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono"
              >
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
                <option>Critical</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono"
              >
                <option>Low</option>
                <option>Normal</option>
                <option>High</option>
                <option>Urgent</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="save-alert-button"
              onClick={createAlert}
              className="btn-primary px-6 py-2"
            >
              SAVE ALERT
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="btn-secondary px-6 py-2"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-6">
        {alerts.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {alerts.map((alert) => (
              <div key={alert.id} className="glass-panel tactical-border p-4" data-testid="alert-card">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-[#00FF9D]" />
                    <span className={`px-2 py-1 text-[10px] font-bold uppercase border ${
                      alert.priority === 'Urgent' ? 'text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10' : 'text-[#00FF9D] border-[#00FF9D]/30 bg-[#00FF9D]/10'
                    }`}>
                      {alert.priority}
                    </span>
                  </div>
                  <button
                    data-testid="delete-alert-button"
                    onClick={() => deleteAlert(alert.id)}
                    className="text-zinc-500 hover:text-[#FF3B30] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Keywords:</span>
                    <p className="text-zinc-300 mt-1">{alert.keywords.join(', ')}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Regions:</span>
                    <p className="text-zinc-300 mt-1">{alert.regions.join(', ')}</p>
                  </div>
                  <div className="flex gap-4 text-[10px] font-mono text-zinc-500">
                    <span>Threat: {alert.threat_level}</span>
                    <span>Status: {alert.active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 font-mono text-sm">No alerts configured</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}